import type { ApolloClient } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import {
    AtomicStatusDocument,
    AtomicStatusType,
} from '../../graphql/generated';

const IDV_LOADING_TIMEOUT = 30 * 1_000; // 30s

export const useIdvStatusPoller = ({
    apolloClient,
    onComplete,
}: {
    apolloClient: ApolloClient<unknown>;
    onComplete: (status: AtomicStatusType) => void;
}) => {
    const mountedAt = useMemo(() => Date.now(), []);

    useEffect(() => {
        const interval = setInterval(() => {
            apolloClient
                .query({
                    query: AtomicStatusDocument,
                    // Execute the query without first checking the cache.
                    // The query's result is NOT stored in the cache.
                    fetchPolicy: 'network-only',
                })
                .then((result) => {
                    const status = result.data.atomicStatus.status;
                    const timestamp = Date.now();
                    const timeoutUnexpired =
                        timestamp - mountedAt < IDV_LOADING_TIMEOUT;

                    if (
                        timeoutUnexpired &&
                        // If status still failed/doc upload, or pending,
                        // continue retrying.
                        (status === 'VERIFICATION_PROCESSING' ||
                            status === 'VERIFICATION_FAILED' ||
                            status === 'VERIFICATION_NEEDS_DOC_UPLOAD')
                    ) {
                        return;
                    }

                    // If timeout surpassed or status is terminal, exit this page.
                    onComplete(status);
                    clearInterval(interval);
                });
        }, 2000);
        return () => clearInterval(interval);
    }, [apolloClient, mountedAt, onComplete]);
};
